import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Check, ArrowLeft, CalendarCheck, Clock, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import agentLars from "@/assets/expert-niels.jpg";
import agentSofia from "@/assets/agent-sofia.jpg";
import agentMarco from "@/assets/agent-marco.jpg";
import type { Agent, LeadData } from "./types";
import { storeAndSendBooking } from "@/lib/booking";

const AGENTS: Agent[] = [
  {
    id: "lNielsAckermann",
    name: "Rudolf Martin",
    role: "Leitender Experte für Fraud-Recovery",
    bio: "15+ Jahre Erfahrung in der Aufklärung internationaler Anlagebetrugsfälle.",
    image: agentLars,
    specialties: ["Krypto-Scams", "Boiler Rooms", "CFD-Betrug"],
  },
  {
    id: "BenjaminLange",
    name: "Benjamin Lange",
    role: "Blockchain-Forensiker",
    bio: "Verfolgt BTC-, ETH- und Stablecoin-Pfade mit Chainalysis & TRM Labs.",
    image: agentMarco,
    specialties: ["On-Chain Analyse", "Wallet-Tracing", "Off-Ramp Identifikation"],
  },
];

const TIME_SLOTS = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "17:00"];

type Step = "agent" | "schedule" | "success";

export function BookingDialog({
  open,
  onOpenChange,
  lead,
  onComplete,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  lead: LeadData | null;
  onComplete?: () => void;
}) {
  const [step, setStep] = useState<Step>("agent");
  const [agent, setAgent] = useState<Agent | null>(null);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStep("agent");
      setAgent(null);
      setDate(undefined);
      setTime(null);
    }
  }, [open]);

  const formattedDate = useMemo(
    () =>
      date
        ? date.toLocaleDateString("de-DE", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : "",
    [date],
  );

  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    if (!agent || !date || !time || !lead) return;
    setSaving(true);
    try {
      const { savedBooking, sent, error } = await storeAndSendBooking({
        lead: {
          fullName: lead.fullName,
          email: lead.email,
          phone: lead.phone,
          betrugsart: lead.betrugsart,
          damageAmount: lead.damageAmount,
          message: lead.message,
        },
        booking: {
          agentId: agent.id,
          agentName: agent.name,
          dateISO: date.toISOString(),
          time,
        },
      });

      if (!sent) {
        console.warn("Booking saved locally but sending failed:", error);
      }

      // proceed to success screen regardless so user sees confirmation
      setStep("success");
      // notify parent to reset stored lead/form if desired
      onComplete?.();
    } catch (err) {
      console.error("Booking failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] overflow-y-auto p-0 sm:w-full sm:max-w-3xl">
        <div className="border-b border-border bg-secondary/40 px-4 py-4 sm:px-6 sm:py-5">
          <DialogHeader className="space-y-1.5 text-left">
            <DialogTitle className="pr-8 text-base sm:text-xl">
              {step === "agent" && "Wählen Sie Ihren Ermittler"}
              {step === "schedule" && "Termin auswählen"}
              {step === "success" && "Termin bestätigt"}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {step === "agent" && "Jeder unserer Spezialisten ist auf einen Bereich des Recovery-Prozesses ausgerichtet."}
              {step === "schedule" && agent && `Verfügbare Termine bei ${agent.name}.`}
              {step === "success" && "Wir haben Ihnen eine Bestätigung per E-Mail gesendet."}
            </DialogDescription>
          </DialogHeader>

          <Stepper step={step} />
        </div>

        <div className="p-4 sm:p-6">
          {step === "agent" && (
            <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-2">
              {AGENTS.map((a) => (
                <button
                  key={a.id}
                  onClick={() => { setAgent(a); setStep("schedule"); }}
                  className={cn(
                    "group flex gap-3 rounded-xl border border-border bg-card p-3 text-left transition-all hover:border-gold hover:shadow-[var(--shadow-elegant)] sm:flex-col sm:p-4 sm:hover:-translate-y-1",
                  )}
                >
                  <img src={a.image} alt={a.name} width={400} height={400} loading="lazy" className="h-16 w-16 shrink-0 rounded-lg object-cover sm:mb-3 sm:aspect-square sm:h-auto sm:w-full" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold leading-tight">{a.name}</p>
                    <p className="text-xs text-muted-foreground">{a.role}</p>
                    <p className="mt-1.5 hidden text-sm text-foreground/80 sm:block">{a.bio}</p>
                    <div className="mt-2 flex flex-wrap gap-1 sm:mt-3 sm:gap-1.5">
                      {a.specialties.map((s) => (
                        <span key={s} className="rounded-full bg-secondary px-2 py-0.5 text-[10px] text-secondary-foreground sm:text-[11px]">{s}</span>
                      ))}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {step === "schedule" && agent && (
            <div className="grid gap-5 md:grid-cols-[auto_1fr] md:gap-6">
              <div className="flex justify-center md:block">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return d < today || d.getDay() === 0 || d.getDay() === 6;
                  }}
                  className="rounded-md border border-border"
                />
              </div>
              <div>
                <p className="mb-3 flex items-center gap-2 text-xs font-medium sm:text-sm">
                  <Clock className="h-4 w-4 text-gold" />
                  {date ? `Verfügbare Zeiten am ${formattedDate}` : "Bitte wählen Sie zuerst ein Datum."}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      disabled={!date}
                      onClick={() => setTime(t)}
                      className={cn(
                        "rounded-md border border-border bg-card px-1 py-2 text-xs font-medium transition-colors sm:text-sm",
                        "hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-40",
                        time === t && "border-gold bg-gold text-navy-deep hover:text-navy-deep",
                      )}
                    >
                      {t}
                    </button>
                  ))}
                </div>

                <div className="mt-6 flex flex-col-reverse items-stretch gap-2 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  <Button variant="ghost" size="sm" onClick={() => setStep("agent")}>
                    <ArrowLeft className="h-4 w-4" /> Ermittler wechseln
                  </Button>
                  <Button variant="gold" disabled={!date || !time} onClick={confirm}>
                    <CalendarCheck className="h-4 w-4" /> Termin bestätigen
                  </Button>
                </div>
              </div>
            </div>
          )}

          {step === "success" && agent && date && time && (
            <div className="py-6 text-center">
              <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-gold/15">
                <Check className="h-8 w-8 text-gold" />
              </div>
              <h3 className="mt-5 text-2xl font-bold">
                Vielen Dank{lead?.fullName ? `, ${lead.fullName.split(" ")[0]}` : ""}!
              </h3>
              <p className="mt-2 text-muted-foreground">
                Ihr persönliches Beratungsgespräch ist gebucht.
              </p>

              <div className="mx-auto mt-6 max-w-sm rounded-xl border border-border bg-secondary/40 p-5 text-left">
                <div className="flex items-center gap-3">
                  <img src={agent.image} alt={agent.name} width={48} height={48} className="h-12 w-12 rounded-md object-cover" />
                  <div>
                    <p className="font-semibold">{agent.name}</p>
                    <p className="text-xs text-muted-foreground">{agent.role}</p>
                  </div>
                </div>
                <div className="mt-4 space-y-1.5 border-t border-border pt-4 text-sm">
                  <p className="flex items-center gap-2">
                    <CalendarCheck className="h-4 w-4 text-gold" /> {formattedDate}
                  </p>
                  <p className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gold" /> {time} Uhr (CET)
                  </p>
                  {lead?.phone && (
                    <p className="flex items-center gap-2">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M22 16.92V21a1 1 0 0 1-1.11 1A19 19 0 0 1 3 5.11 1 1 0 0 1 4 4h4.09a1 1 0 0 1 1 .75 12.05 12.05 0 0 0 .7 2.81 1 1 0 0 1-.24 1L8.91 10.91a16 16 0 0 0 6.18 6.18l1.35-1.35a1 1 0 0 1 1-.24 12.05 12.05 0 0 0 2.81.7 1 1 0 0 1 .75 1V21z"/></svg>
                      {lead.phone}
                    </p>
                  )}
                  {lead?.betrugsart && (
                    <p className="flex items-center gap-2">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 6 2-7L2 9h7z"/></svg>
                      {lead.betrugsart}
                    </p>
                  )}
                  {lead?.damageAmount && (
                    <p className="flex items-center gap-2">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M12 1v22M17 5H9a4 4 0 0 0 0 8h6a4 4 0 0 1 0 8H7"/></svg>
                      {lead.damageAmount}
                    </p>
                  )}
                  {lead?.message && (
                    <div className="mt-2 rounded-md bg-card/50 p-2 text-sm">
                      <strong className="text-xs">Fallbeschreibung:</strong>
                      <p className="mt-1 whitespace-pre-wrap text-sm">{lead.message}</p>
                    </div>
                  )}
                </div>
              </div>

              <p className="mt-6 inline-flex items-center gap-2 text-xs text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-gold" />
                Eine Bestätigung wurde an {lead?.email || "Ihre E-Mail"} gesendet.
              </p>

              <div className="mt-6">
                <Button variant="navy" onClick={() => { onOpenChange(false); onComplete?.(); }}>Schließen</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stepper({ step }: { step: Step }) {
  const steps: { id: Step; label: string }[] = [
    { id: "agent", label: "Ermittler" },
    { id: "schedule", label: "Termin" },
    { id: "success", label: "Bestätigung" },
  ];
  const idx = steps.findIndex((s) => s.id === step);
  return (
    <div className="mt-4 flex items-center gap-2">
      {steps.map((s, i) => (
        <div key={s.id} className="flex flex-1 items-center gap-2">
          <div
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
              i <= idx ? "bg-gold text-navy-deep" : "bg-border text-muted-foreground",
            )}
          >
            {i < idx ? <Check className="h-3.5 w-3.5" /> : i + 1}
          </div>
          <span className={cn("hidden text-xs font-medium sm:inline", i <= idx ? "text-foreground" : "text-muted-foreground")}>
            {s.label}
          </span>
          {i < steps.length - 1 && (
            <div className={cn("h-px flex-1", i < idx ? "bg-gold" : "bg-border")} />
          )}
        </div>
      ))}
    </div>
  );
}